import BigNumber from 'bignumber.js';

type TPSLV2Params = {
    entryPrice: number;
    amount: number;
    leverage: number;
    typeTrade: 'Long' | 'Short';
    decimalsToShow?: number;
    tpPrice?: number;
    slPrice?: number;
    tpPercent?: number;
    slPercent?: number;
};

type TPSLV2Result = {
    tpPrice?: number;
    slPrice?: number;
    liqPrice?: number;
    tpPercent?: number;
    slPercent?: number;
    tpPnL?: number;
    slPnL?: number;
    positionSize: number;
};

// percent = (targetPrice - entryPrice) / entryPrice * 100 * leverage
// → Long TP (price > entry): percent > 0
// → Long SL (price < entry): percent < 0
// → Short TP (price < entry): percent < 0
// → Short SL (price > entry): percent > 0

function priceToPercent(targetPrice: BigNumber, entryPrice: BigNumber, leverage: BigNumber): BigNumber {
    return targetPrice.minus(entryPrice).dividedBy(entryPrice).multipliedBy(100).multipliedBy(leverage);
}

function percentToPrice(percent: BigNumber, entryPrice: BigNumber, leverage: BigNumber): BigNumber {
    return entryPrice.multipliedBy(
        new BigNumber(1).plus(percent.dividedBy(100).dividedBy(leverage))
    );
}

function roundPrice(bn: BigNumber, decimals: number): number {
    return bn.decimalPlaces(decimals, BigNumber.ROUND_HALF_UP).toNumber();
}

function roundPercent(bn: BigNumber): number {
    return bn.decimalPlaces(2, BigNumber.ROUND_HALF_UP).toNumber();
}

export function calculateTPSLV2({
    entryPrice: entryPriceRaw,
    amount: amountRaw,
    leverage: leverageRaw,
    typeTrade,
    decimalsToShow = 6,
    tpPrice: tpPriceInput,
    slPrice: slPriceInput,
    tpPercent: tpPercentInput,
    slPercent: slPercentInput,
}: TPSLV2Params): TPSLV2Result {
    const entry = new BigNumber(entryPriceRaw);
    const amount = new BigNumber(amountRaw);
    const leverage = new BigNumber(leverageRaw);
    const positionSize = amount.multipliedBy(leverage);

    let tpPercent: BigNumber | undefined;
    let slPercent: BigNumber | undefined;
    let tpPrice: BigNumber | undefined;
    let slPrice: BigNumber | undefined;

    // For Short: profit = price goes down, so negate the raw price→percent direction
    // so that positive percent always means profit regardless of side.
    const sign = typeTrade === 'Long' ? 1 : -1;

    // TP
    if (typeof tpPriceInput !== 'undefined' && tpPriceInput > 0) {
        tpPrice = new BigNumber(tpPriceInput);
        tpPercent = priceToPercent(tpPrice, entry, leverage).multipliedBy(sign);
    } else if (typeof tpPercentInput !== 'undefined') {
        tpPercent = new BigNumber(tpPercentInput);
        tpPrice = percentToPrice(tpPercent.multipliedBy(sign), entry, leverage);
    }

    // SL
    if (typeof slPriceInput !== 'undefined' && slPriceInput > 0) {
        slPrice = new BigNumber(slPriceInput);
        slPercent = priceToPercent(slPrice, entry, leverage).multipliedBy(sign);
    } else if (typeof slPercentInput !== 'undefined') {
        slPercent = new BigNumber(slPercentInput);
        slPrice = percentToPrice(slPercent.multipliedBy(sign), entry, leverage);
    }

    // Liquidation price (direction depends on type)
    const liqRatio = new BigNumber(90).dividedBy(100).dividedBy(leverage);
    const liqPrice = typeTrade === 'Long'
        ? entry.multipliedBy(new BigNumber(1).minus(liqRatio))
        : entry.multipliedBy(new BigNumber(1).plus(liqRatio));

    const tpPercentNum = tpPercent != null ? roundPercent(tpPercent) : undefined;
    const slPercentNum = slPercent != null ? roundPercent(slPercent) : undefined;
    const tpPriceNum = tpPrice != null && tpPrice.isGreaterThan(0) ? roundPrice(tpPrice, decimalsToShow) : 0;
    const slPriceNum = slPrice != null && slPrice.isGreaterThan(0) ? roundPrice(slPrice, decimalsToShow) : 0;
    const amountNum = amountRaw;

    return {
        positionSize: positionSize.toNumber(),
        tpPrice: tpPriceNum,
        slPrice: slPriceNum,
        liqPrice: roundPrice(liqPrice, decimalsToShow),
        ...(tpPercentNum != null ? { tpPercent: tpPercentNum, tpPnL: (tpPercentNum / 100) * amountNum } : {}),
        ...(slPercentNum != null ? { slPercent: slPercentNum, slPnL: (slPercentNum / 100) * amountNum } : {}),
    };
}
